import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { accessState, trialMsLeft, formatLeft } from '../access';
import { markTrialExpired } from '../session';

/*
 * A strip across the top of every page while an account is on a free trial.
 *
 * It says three things and nothing else: how long is left, what the trial does
 * not include, and where to go to keep going. Someone evaluating the product
 * should never discover a limit by being refused — they should have been told.
 *
 * The countdown runs locally from the end time the server gave at sign-in.
 * When it reaches zero the app moves to the plans without waiting for the next
 * request to be refused; the server would refuse it regardless.
 */
export default function TrialBar() {
  const location = useLocation();
  const [state, setState] = useState(accessState);
  const [left, setLeft] = useState(trialMsLeft);

  useEffect(() => {
    if (state !== 'trial') return undefined;
    const tick = () => {
      const ms = trialMsLeft();
      setLeft(ms);
      if (ms <= 0) {
        markTrialExpired();
        setState('trial_expired');
      }
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [state]);

  /* The server can end the trial too (the guard records it); pick that up on
     navigation rather than polling storage. */
  useEffect(() => { setState(accessState()); }, [location.pathname]);

  if (state !== 'trial' && state !== 'trial_expired') return null;

  const onPlans = location.pathname.indexOf('/plans') === 0;

  if (state === 'trial_expired') {
    return (
      <div className="trialbar trialbar-ended" role="status">
        <span className="trialbar-dot" aria-hidden="true" />
        <span className="trialbar-text">
          <b>Your free trial has ended.</b> Choose a plan to keep searching, saving lists and talking to players.
        </span>
        {!onPlans && <Link className="trialbar-cta" to="/plans">See plans</Link>}
      </div>
    );
  }

  return (
    <div className="trialbar" role="status">
      <span className="trialbar-dot" aria-hidden="true" />
      <span className="trialbar-text">
        <b>Free trial — {formatLeft(left)} left.</b>{' '}
        Saving lists, messaging players and exporting are not included.
      </span>
      {!onPlans && <Link className="trialbar-cta" to="/plans">See plans</Link>}
    </div>
  );
}
